import isEmpty from 'lodash/isEmpty';
import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { BQLink } from '../../../../atoms/bqlink';
import { ArrowRightIcon, OrderAlphabeticalAscendingIcon } from '../../../../atoms/material-icons';
import EmptyState from '../empty-state';
import { isWithinTradingHours, widgetLink } from '../../../../../helpers/utils';
import { useSelector } from 'react-redux';
import get from 'lodash/get';
import './top-sectors.m.css';

const TopSectors = ({ widget = false, container = '' }) => {
  const widgetParam = widget ? '?view=apps' : '';
  const [topSectorsData, setTopSectorsData] = useState({});
  const [filteredData, setFilteredData] = useState({});
  const [tableData, setTableData] = useState([]);
  const [isNseSelected, setIsNseSelected] = useState(true);
  const [isAdvancesSelected, setIsAdvancesSelected] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const tradingHolidays = useSelector((state) =>
    get(state, ['qt', 'config', 'features', 'trading_holidays'], []),
  );
  const specialTradingDays = useSelector((state) =>
    get(state, ['qt', 'config', 'features', 'special_trading_days'], {}),
  );
  const [isDarkMode, setIsDarkMode] = useState(false);

  const getTopSectorsData = async () => {
    const response = await fetch('/feapi/markets/top-sectors');
    return response.json();
  };

  const resultTopSectors = useQuery('getTopSectorsData', () => getTopSectorsData(), {
    refetchInterval: 30000,
    refetchIntervalInBackground: true,
    onSuccess: (data) => {
      setTopSectorsData(data);
    },
    enabled: initialLoading || isWithinTradingHours(tradingHolidays, specialTradingDays),
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setInitialLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const getTopSectorsData = async () => {
      const response = await fetch('/feapi/markets/top-sectors');
      const data = await response.json();

      setTopSectorsData(data);
      setFilteredData(data?.data?.nse);
      setTableData(data?.data?.nse['adv-sectors']);
      setIsNseSelected(true);
      setIsAdvancesSelected(true);
    };
    getTopSectorsData();
  }, []);

  useEffect(() => {
    if (resultTopSectors?.isSuccess) {
      setTopSectorsData(resultTopSectors?.data?.data);
    }
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    setIsDarkMode(mediaQuery.matches);

    const handleChange = (e) => setIsDarkMode(e.matches);
    mediaQuery.addEventListener('change', handleChange);

    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const nseHandler = () => {
    setFilteredData(topSectorsData?.data?.nse);
    setTableData(topSectorsData?.data?.nse['adv-sectors']);
    setIsNseSelected(true);
    setIsAdvancesSelected(true);
  };

  const bseHandler = () => {
    setFilteredData(topSectorsData?.data?.bse);
    setTableData(topSectorsData?.data?.bse['adv-sectors']);
    setIsNseSelected(false);
    setIsAdvancesSelected(true);
  };

  const advancesBtnHandler = () => {
    setTableData(filteredData['adv-sectors']);
    setIsAdvancesSelected(true);
  };

  const declinesBtnHandler = () => {
    setTableData(filteredData['dec-sectors']);
    setIsAdvancesSelected(false);
  };

  if (initialLoading) {
    return <div styleName="initial-loading-container"></div>;
  }

  return (
    <div styleName={`top-sectors-container ${widget ? 'widget' : ''}`}>
      <div styleName="header-title-container">
        <OrderAlphabeticalAscendingIcon size={'26px'} color={'#d01e51'} />
        <h4 styleName="list-header-title">TOP SECTORS</h4>
      </div>
      <div styleName="index-btn-container">
        <button styleName={`index-btn ${isNseSelected ? 'selected' : ''}`} onClick={nseHandler}>
          NSE
        </button>
        <button
          styleName={`index-btn ${isNseSelected === false ? 'selected' : ''}`}
          onClick={bseHandler}
        >
          BSE
        </button>
      </div>
      {isEmpty(topSectorsData) || isEmpty(filteredData) ? (
        <div styleName="no-data-container-ext">
          <div styleName="no-data-container-int">
            <EmptyState />
          </div>
        </div>
      ) : (
        <>
          <div styleName="statistics-container">
            <div styleName="gainer-container">
              <p styleName="gainer-title">Top Gainer</p>
              <div styleName="stat-name">
                {filteredData['adv-sectors'] && filteredData?.['adv-sectors']?.[0]?.name
                  ? filteredData?.['adv-sectors']?.[0]?.name
                  : '-'}
              </div>
              <div styleName="stat-value-positive">
                &#9650; {filteredData['adv-sectors'] && filteredData?.['adv-sectors']?.[0]?.chgp}%
              </div>
            </div>
            <div styleName="loser-container">
              <p styleName="loser-title">Top Loser</p>
              <div styleName="stat-name-alt">
                {filteredData['dec-sectors'] && filteredData?.['dec-sectors']?.[0]?.name
                  ? filteredData?.['dec-sectors']?.[0]?.name
                  : '-'}
              </div>
              <div styleName="stat-value-negative">
                &#9660; {filteredData['dec-sectors'] && filteredData?.['dec-sectors']?.[0]?.chgp}%
              </div>
            </div>
          </div>
          <div styleName="adv-dec-btn-container">
            <button
              styleName={`${isAdvancesSelected ? 'selected-btn' : ''} `}
              onClick={advancesBtnHandler}
            >
              Advances
            </button>
            <button
              styleName={`${isAdvancesSelected === false ? 'selected-btn' : ''} `}
              onClick={declinesBtnHandler}
            >
              Declines
            </button>
          </div>
          <div styleName="table-container">
            <table styleName="table-parent">
              {tableData.length > 0 ? (
                tableData.map((data, index) => (
                  <tbody key={index}>
                    <tr>
                      <td styleName="indice-name">{data.name}</td>
                      <td styleName="stat-name-percentage">{data.chgp}%</td>
                    </tr>
                  </tbody>
                ))
              ) : (
                <tbody>
                  <div styleName="no-data-container-ext-alt">
                    <div styleName="no-data-container-int">
                      <EmptyState />
                    </div>
                  </div>
                </tbody>
              )}
            </table>
          </div>
          {(!widget || (widget && container === 'full')) && (
            <div>
              <BQLink
                styleName="top-sectors-btn"
                href={widgetLink(`/markets/equities/sectoral-indices${widgetParam}`, widget)}
                target="_blank"
                rel="noreferrer"
              >
                Show All Sectors{' '}
                <ArrowRightIcon
                  color={isDarkMode && widget ? '#FFFFFF' : '#2b2b2b'}
                  styleName="tab-icon"
                />
              </BQLink>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TopSectors;
